import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MAX_ATTACHMENTS, MEDIA_ACCEPT, isVideo, screenFiles } from '../../lib/publicMedia';

/**
 * Photo / video picker with thumbnails, shared by the feedback form and by
 * survey questions of type "photo".
 *
 * Owns only the choosing; the parent uploads on submit, so a guest who
 * abandons the form never leaves orphaned objects in the bucket.
 */
export default function MediaPicker({ files, onChange, max = MAX_ATTACHMENTS, compact = false }) {
  const { t } = useTranslation();
  const inputRef = useRef(null);
  const [error, setError] = useState('');

  // Object URLs must be revoked or every re-render leaks one. The old inline
  // version called createObjectURL straight in the JSX and never revoked.
  const [previews, setPreviews] = useState([]);
  useEffect(() => {
    const urls = files.map((f) => (isVideo(f) ? null : URL.createObjectURL(f)));
    setPreviews(urls);
    return () => urls.forEach((u) => u && URL.revokeObjectURL(u));
  }, [files]);

  const handleSelect = (e) => {
    const { accepted, rejected } = screenFiles(e.target.files, files.length, max);
    setError(rejected ? t('media.fileTooLarge', { name: rejected.name }) : '');
    if (accepted.length) onChange([...files, ...accepted]);
    if (inputRef.current) inputRef.current.value = '';
  };

  const removeAt = (index) => {
    setError('');
    onChange(files.filter((_, i) => i !== index));
  };

  return (
    <div>
      {error && <p className="mb-3 rounded-xl bg-red-50 p-2.5 text-sm text-red-700" role="alert">{error}</p>}

      {files.length > 0 && (
        <div className="grid grid-cols-4 gap-2 mb-3">
          {files.map((file, index) => (
            <div key={index} className="relative aspect-square overflow-hidden rounded-xl border border-brand-line bg-white">
              {isVideo(file) ? (
                <div className="flex h-full w-full items-center justify-center bg-brand-ink">
                  <svg className="w-8 h-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3l14 9-14 9V3z" />
                  </svg>
                  <span className="absolute bottom-1 left-1 text-white text-[10px] bg-black/50 px-1 rounded">
                    {t('media.videoBadge')}
                  </span>
                </div>
              ) : (
                <img src={previews[index]} alt="" className="w-full h-full object-cover" />
              )}
              <button
                type="button"
                onClick={() => removeAt(index)}
                aria-label={t('media.remove')}
                className="absolute top-1 right-1 w-5 h-5 bg-black/60 rounded-full flex items-center justify-center text-white text-xs hover:bg-black/80"
              >
                &times;
              </button>
            </div>
          ))}
        </div>
      )}

      {files.length < max && (
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          className={`flex w-full items-center justify-center gap-3 rounded-2xl border-2 border-dashed border-brand-peachLine bg-white/60 text-brand-body transition-colors hover:border-brand-clay hover:bg-brand-peach/40 ${compact ? 'py-4' : 'py-6'}`}
        >
          <svg className="h-6 w-6 flex-none text-brand-clay/70" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.7}>
            <rect x="3" y="5" width="18" height="14" rx="2" />
            <circle cx="9" cy="10" r="1.6" />
            <path d="m4 17 4.5-4.5a2 2 0 0 1 2.8 0L16 17" />
          </svg>
          <span className="text-left">
            <span className="block text-sm font-semibold text-brand-clay">{t('media.add')}</span>
            <span className="block text-xs text-brand-body/80">{files.length}/{max}</span>
          </span>
        </button>
      )}

      <input
        ref={inputRef}
        type="file"
        accept={MEDIA_ACCEPT}
        multiple
        onChange={handleSelect}
        className="hidden"
      />
    </div>
  );
}
